// Targeted Count - Review and Finalise
import React, { useState, useMemo } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  ArrowLeft,
  CheckCircle2,
  AlertCircle,
  Target,
  TrendingUp,
  TrendingDown,
  FileText,
  Download,
  Save,
} from "lucide-react";
import { useNavigation } from "@/components/layout/app-layout";
import { toast } from "sonner";
import { format } from "date-fns";
import {
  saveStockTake,
  generateStockTakeId,
  generateStockTakeReference,
  SavedStockTake,
} from "@/lib/stock-take-storage";

interface CountItem {
  code: string;
  description: string;
  category: string;
  supplier: string;
  location: string;
  systemQty: number;
  unit: string;
  value: number;
  countedQty: string;
  countedBy: string;
  notes: string;
  isCounted: boolean;
}

interface PartialReviewPageProps {
  setupData?: {
    formData: {
      date: Date;
      warehouse: string;
      assignedTo: string;
      description: string;
      notes: string;
    };
    filters: {
      category: string;
      supplier: string;
      minValue: string;
      maxValue: string;
      location: string;
      includeZeroQty: boolean;
    };
  };
  countedItems?: CountItem[];
}

export function StockTakesPartialReviewPage({ setupData, countedItems = [] }: PartialReviewPageProps) {
  const { navigateTo } = useNavigation();
  const [finalNotes, setFinalNotes] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Calculate statistics
  const stats = useMemo(() => {
    const counted = countedItems.filter(item => item.isCounted);
    const totalItems = countedItems.length;
    const countedItems_count = counted.length;
    
    const variances = counted.filter(item => {
      const countedQty = parseFloat(item.countedQty);
      return !isNaN(countedQty) && countedQty !== item.systemQty;
    });
    
    const increases = variances.filter(item => {
      const countedQty = parseFloat(item.countedQty);
      return countedQty > item.systemQty;
    });
    
    const decreases = variances.filter(item => {
      const countedQty = parseFloat(item.countedQty);
      return countedQty < item.systemQty;
    });
    
    const totalVarianceQty = variances.reduce((sum, item) => {
      const countedQty = parseFloat(item.countedQty);
      return sum + Math.abs(countedQty - item.systemQty);
    }, 0);
    
    const totalVarianceValue = variances.reduce((sum, item) => {
      const countedQty = parseFloat(item.countedQty);
      const variance = countedQty - item.systemQty;
      return sum + Math.abs(variance * item.value);
    }, 0);

    return {
      totalItems,
      countedItems: countedItems_count,
      varianceCount: variances.length,
      increases: increases.length,
      decreases: decreases.length,
      totalVarianceQty,
      totalVarianceValue,
    };
  }, [countedItems]);

  // Get items with variances
  const varianceItems = useMemo(() => {
    return countedItems
      .filter(item => {
        if (!item.isCounted) return false;
        const countedQty = parseFloat(item.countedQty);
        return !isNaN(countedQty) && countedQty !== item.systemQty;
      })
      .map(item => {
        const countedQty = parseFloat(item.countedQty);
        const variance = countedQty - item.systemQty;
        const varianceValue = variance * item.value;
        return {
          ...item,
          variance,
          varianceValue,
        };
      })
      .sort((a, b) => Math.abs(b.varianceValue) - Math.abs(a.varianceValue));
  }, [countedItems]);

  const getVarianceBadge = (variance: number) => {
    if (variance === 0) {
      return <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">Match</Badge>;
    }
    if (variance > 0) {
      return (
        <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
          <TrendingUp className="h-3 w-3 mr-1" />
          +{variance}
        </Badge>
      );
    }
    return (
      <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
        <TrendingDown className="h-3 w-3 mr-1" />
        {variance}
      </Badge>
    );
  };

  const handleExportReport = () => {
    toast.success("Report exported successfully");
  };

  const handleSaveDraft = () => {
    toast.success("Draft saved successfully");
  };

  const handleFinalize = async () => {
    setIsSubmitting(true);
    
    try {
      // Create stock take record
      const stockTakeId = generateStockTakeId();
      const reference = generateStockTakeReference();
      
      const stockTake: SavedStockTake = {
        id: stockTakeId,
        reference,
        type: "Targeted Count",
        status: "Completed",
        description: setupData?.formData?.description || "Targeted Count",
        date: setupData?.formData?.date || new Date(),
        warehouse: setupData?.formData?.warehouse || "",
        assignedTo: setupData?.formData?.assignedTo || "",
        notes: setupData?.formData?.notes || "",
        finalNotes,
        filters: setupData?.filters,
        items: countedItems.map(item => ({
          code: item.code,
          description: item.description,
          category: item.category,
          location: item.location,
          systemQty: item.systemQty,
          countedQty: item.countedQty ? parseFloat(item.countedQty) : 0,
          unit: item.unit,
          variance: item.countedQty ? parseFloat(item.countedQty) - item.systemQty : 0,
          countedBy: item.countedBy,
          notes: item.notes,
        })),
        statistics: {
          totalItems: stats.totalItems,
          countedItems: stats.countedItems,
          varianceCount: stats.varianceCount,
          increases: stats.increases,
          decreases: stats.decreases,
          totalVarianceValue: stats.totalVarianceValue,
        },
        createdAt: new Date(),
        completedAt: new Date(),
      };
      
      // Save to storage
      saveStockTake(stockTake);
      
      // Show success message
      toast.success("Stock take finalised successfully", {
        description: `Reference: ${reference}`,
      });
      
      // Navigate to stock takes list
      setTimeout(() => {
        navigateTo("stock-takes", { refresh: true });
      }, 1000);
      
    } catch (error) {
      console.error("Error finalising stock take:", error);
      toast.error("Failed to finalise stock take");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigateTo("stock-takes/targeted/count", { setupData })}
          className="gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Count
        </Button>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleExportReport}
            className="gap-2"
          >
            <Download className="h-4 w-4" />
            Export Report
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleSaveDraft}
            className="gap-2"
          >
            <Save className="h-4 w-4" />
            Save Draft
          </Button>
        </div>
      </div>

      {/* Title Section */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 rounded-lg bg-green-50 dark:bg-green-900/20">
            <Target className="h-6 w-6 text-green-600 dark:text-green-400" />
          </div>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Review & Finalise</h1>
            <p className="text-muted-foreground">
              {setupData?.formData?.description || "Partial Stock Take"}
            </p>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="pt-6">
            <div className="space-y-1">
              <p className="text-sm font-medium text-muted-foreground">Items Counted</p>
              <p className="text-2xl font-bold">{stats.countedItems} / {stats.totalItems}</p>
              <p className="text-xs text-muted-foreground">
                {((stats.countedItems / stats.totalItems) * 100).toFixed(0)}% complete
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="space-y-1">
              <p className="text-sm font-medium text-muted-foreground">Variances Found</p>
              <p className="text-2xl font-bold text-amber-600">{stats.varianceCount}</p>
              <p className="text-xs text-muted-foreground">
                {stats.increases} increases, {stats.decreases} decreases
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="space-y-1">
              <p className="text-sm font-medium text-muted-foreground">Total Variance Qty</p>
              <p className="text-2xl font-bold">{stats.totalVarianceQty.toFixed(0)}</p>
              <p className="text-xs text-muted-foreground">
                Absolute quantity difference
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="space-y-1">
              <p className="text-sm font-medium text-muted-foreground">Variance Value</p>
              <p className="text-2xl font-bold text-red-600">
                ${stats.totalVarianceValue.toFixed(2)}
              </p>
              <p className="text-xs text-muted-foreground">
                Financial impact
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Stock Take Details */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Stock Take Details</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Description</p>
              <p className="text-sm">{setupData?.formData?.description || "N/A"}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">Date</p>
              <p className="text-sm">
                {setupData?.formData?.date ? format(setupData.formData.date, "PPP") : "N/A"}
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">Warehouse</p>
              <p className="text-sm">{setupData?.formData?.warehouse || "All Warehouses"}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">Assigned To</p>
              <p className="text-sm">{setupData?.formData?.assignedTo || "N/A"}</p>
            </div>
          </div>
          
          {setupData?.filters && (
            <div className="mt-4 pt-4 border-t">
              <p className="text-sm font-medium text-muted-foreground mb-2">Applied Filters</p>
              <div className="flex flex-wrap gap-2">
                {setupData.filters.category && (
                  <Badge variant="outline">Category: {setupData.filters.category}</Badge>
                )}
                {setupData.filters.supplier && (
                  <Badge variant="outline">Supplier: {setupData.filters.supplier}</Badge>
                )}
                {setupData.filters.minValue && (
                  <Badge variant="outline">Min Value: ${setupData.filters.minValue}</Badge>
                )}
                {setupData.filters.maxValue && (
                  <Badge variant="outline">Max Value: ${setupData.filters.maxValue}</Badge>
                )}
                {setupData.filters.location && (
                  <Badge variant="outline">Location: {setupData.filters.location}</Badge>
                )}
                {setupData.filters.includeZeroQty && (
                  <Badge variant="outline">Including zero qty items</Badge>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Variances Table */}
      {varianceItems.length > 0 && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-amber-600" />
              Items with Variances
            </CardTitle>
            <CardDescription>
              Review all items where counted quantity differs from system quantity
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Code</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead className="text-right">System Qty</TableHead>
                  <TableHead className="text-right">Counted Qty</TableHead>
                  <TableHead className="text-right">Variance</TableHead>
                  <TableHead className="text-right">Value Impact</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {varianceItems.map((item) => (
                  <TableRow key={item.code}>
                    <TableCell className="font-medium">{item.code}</TableCell>
                    <TableCell>{item.description}</TableCell>
                    <TableCell>{item.location}</TableCell>
                    <TableCell className="text-right">
                      {item.systemQty} {item.unit}
                    </TableCell>
                    <TableCell className="text-right">
                      {parseFloat(item.countedQty)} {item.unit}
                    </TableCell>
                    <TableCell className="text-right">
                      {getVarianceBadge(item.variance)}
                    </TableCell>
                    <TableCell className="text-right">
                      <span className={item.varianceValue < 0 ? "text-red-600" : "text-blue-600"}>
                        ${Math.abs(item.varianceValue).toFixed(2)}
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Final Notes */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Final Notes
          </CardTitle>
          <CardDescription>
            Add any final comments or observations about this stock take
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Textarea
            value={finalNotes}
            onChange={(e) => setFinalNotes(e.target.value)}
            placeholder="Enter any final notes, explanations for variances, or action items..."
            rows={4}
          />
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <div className="flex items-center justify-between pt-6 border-t">
        <Button
          variant="outline"
          onClick={() => navigateTo("stock-takes/targeted/count", { setupData })}
        >
          Back to Count
        </Button>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={handleSaveDraft}
          >
            Save Draft
          </Button>
          <Button
            onClick={handleFinalize}
            disabled={isSubmitting}
            className="gap-2"
          >
            <CheckCircle2 className="h-4 w-4" />
            {isSubmitting ? "Finalising..." : "Finalise Stock Take"}
          </Button>
        </div>
      </div>
    </div>
  );
}
